
import React from 'react';
import { format } from 'date-fns';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, X, Pause } from 'lucide-react';
import { ResellerOrder } from '@/types/resellerOrder';

interface OrderDetailsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedOrder: ResellerOrder | null;
  onApproveOrder: (orderId: string) => void;
  onRejectOrder: (orderId: string) => void;
  onHoldOrder: (orderId: string) => void;
  isProcessing: boolean;
}

const OrderDetailsDialog = ({
  isOpen,
  onOpenChange,
  selectedOrder,
  onApproveOrder,
  onRejectOrder,
  onHoldOrder,
  isProcessing
}: OrderDetailsDialogProps) => {
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (!selectedOrder) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Order Details: {selectedOrder.orderNumber}</DialogTitle>
          <DialogDescription>
            Review complete information about this order
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <h3 className="text-lg font-medium mb-3">Business Information</h3>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Business Name:</span> {selectedOrder.businessName}
              </div>
              <div>
                <span className="font-medium">Business Email:</span> {selectedOrder.businessEmail}
              </div>
              <div>
                <span className="font-medium">Contact Name:</span> {selectedOrder.contactName}
              </div>
              <div>
                <span className="font-medium">Contact Email:</span> {selectedOrder.contactEmail}
              </div>
              <div>
                <span className="font-medium">Phone:</span> {selectedOrder.contactPhone}
              </div>
              
              {selectedOrder.businessAddress && (
                <>
                  <div className="font-medium mt-3">Business Address:</div>
                  <div>{selectedOrder.businessAddress.street}</div>
                  <div>
                    {selectedOrder.businessAddress.city}, {selectedOrder.businessAddress.state} {selectedOrder.businessAddress.zip}
                  </div>
                  <div>{selectedOrder.businessAddress.country}</div>
                </>
              )}
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-medium mb-3">Order Information</h3>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Order ID:</span> {selectedOrder.orderNumber}
              </div>
              <div>
                <span className="font-medium">Date:</span> {format(new Date(selectedOrder.orderDate), 'MMMM d, yyyy')}
              </div>
              <div>
                <span className="font-medium">Amount:</span> {formatCurrency(selectedOrder.paymentAmount)}
              </div>
              <div>
                <span className="font-medium">Plan:</span> {selectedOrder.plan}
              </div>
              <div>
                <span className="font-medium">Payment Method:</span> {selectedOrder.paymentMethod.replace('_', ' ')}
              </div>
              <div>
                <span className="font-medium">Status:</span> 
                <Badge
                  variant="outline"
                  className="ml-2 bg-amber-100 text-amber-800 border-amber-200"
                >
                  Pending
                </Badge>
              </div>
              {selectedOrder.domain && (
                <div>
                  <span className="font-medium">Domain:</span> {selectedOrder.domain}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2 mt-6">
          <div className="flex items-center gap-2">
            <Button
              variant="destructive"
              onClick={() => {
                onRejectOrder(selectedOrder.id);
                onOpenChange(false);
              }}
              disabled={isProcessing}
            >
              <X className="h-4 w-4 mr-2" />
              Reject Order
            </Button>
            <Button
              variant="warning"
              onClick={() => {
                onHoldOrder(selectedOrder.id);
                onOpenChange(false);
              }}
              disabled={isProcessing}
            >
              <Pause className="h-4 w-4 mr-2" />
              Hold Order
            </Button>
          </div>
          <Button
            onClick={() => {
              onApproveOrder(selectedOrder.id);
              onOpenChange(false);
            }}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Approve Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailsDialog;
